function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sanitizeColor } from '@guryou/utils';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputBase from '@material-ui/core/InputBase';
import InputLabel from '@material-ui/core/InputLabel';
import ListSubheader from '@material-ui/core/ListSubheader';
import MenuItem from '@material-ui/core/MenuItem';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import SearchIcon from '@material-ui/icons/Search';
import { Block, FlexRow, LittleCircle } from '../content';
const SelectOption = ({
  name,
  value,
  controlled = false,
  label,
  circle,
  onChange,
  options,
  disabled,
  removeDuplicate = false,
  none = true,
  noneValue,
  throwError = null,
  errorMessage = null,
  margin = 'normal',
  xs,
  md,
  lg,
  fullWidth = false,
  defaultOptions,
  MenuProps,
  searchable = false,
  searchPlaceholder,
  ...rest
}) => {
  const {
    t
  } = useTranslation();
  const inputRef = React.useRef();
  const [localValue, setLocalValue] = useState(value || '');
  const [labelWidth, setLabelWidth] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const labelRef = useCallback(node => {
    if (node !== null) {
      setLabelWidth(node.offsetWidth);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [label]);
  const triggerChangeForHiddenField = value => {
    const element = inputRef.current.getElementsByClassName('MuiSelect-nativeInput')[0];
    if (!element) {
      return;
    }
    const lastValue = element.value;
    element.value = value;
    const tracker = element._valueTracker;
    if (tracker) {
      tracker.setValue(lastValue);
    }
    const event = new Event('input', {
      target: element,
      bubbles: true
    });
    element.dispatchEvent(event);
  };
  const handleChange = event => {
    const currentValue = controlled ? value : localValue;
    if (!currentValue && !event.target.value || currentValue === event.target.value) {
      return;
    }
    onChange && onChange(event.target.value, event.target);
    setLocalValue(event.target.value);
    triggerChangeForHiddenField(event.target.value);
  };
  if (!Array.isArray(options)) {
    return null;
  }
  const inputField = /*#__PURE__*/React.createElement(OutlinedInput, {
    notched: !!label,
    labelWidth: label ? labelWidth : 0,
    name: name,
    disabled: disabled,
    id: name
  });

  // `leading`: an optional node drawn before the label (e.g. a plan's thumbnail).
  let renderOptions = [...options].map(x => ({
    value: x.value || x.id,
    label: x.label || x.name || x.title,
    color: x.color,
    leading: x.leading
  }));
  if (removeDuplicate) {
    renderOptions = [...new Set(renderOptions.map(opt => opt.label))].map(x => ({
      label: x,
      value: options.find(y => y.label === x).value
    }));
  }
  if (defaultOptions) {
    renderOptions = [{
      label: t('GENERAL.showAll'),
      value: 'all'
    }, {
      label: t('GENERAL.withoutGroup'),
      value: 'noGroup'
    }, ...renderOptions];
  }
  const visibleOptions = searchQuery ? renderOptions.filter(option => String(option.label || '').toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase())) : renderOptions;
  const marginStyleProps = !xs && !md ? {
    margin
  } : {};
  const content = /*#__PURE__*/React.createElement(FormControl, _extends({
    variant: "outlined",
    error: !!throwError
  }, marginStyleProps, {
    fullWidth: fullWidth
  }), label && /*#__PURE__*/React.createElement(InputLabel, {
    ref: labelRef,
    htmlFor: name,
    shrink: true
  }, label), /*#__PURE__*/React.createElement(Select, _extends({
    variant: "outlined",
    value: controlled ? value === undefined || value === null ? '' : value : localValue
  }, rest, {
    MenuProps: {
      ...MenuProps,
      classes: {
        ...MenuProps?.classes,
        paper: ['guryou-select-menu', MenuProps?.classes?.paper].filter(Boolean).join(' ')
      },
      MenuListProps: {
        ...MenuProps?.MenuListProps,
        className: ['guryou-select-menu-list', MenuProps?.MenuListProps?.className].filter(Boolean).join(' ')
      }
    },
    onChange: handleChange,
    onClose: event => {
      setSearchQuery('');
      rest.onClose?.(event);
    },
    input: inputField,
    ref: inputRef
  }), searchable && /*#__PURE__*/React.createElement(ListSubheader, {
    disableSticky: true,
    style: {
      padding: '7px 10px',
      background: '#FFFFFF'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      minHeight: 38,
      padding: '0 10px',
      border: '1px solid #DFE2EA',
      borderRadius: 8
    },
    onKeyDown: event => event.stopPropagation(),
    onClick: event => event.stopPropagation()
  }, /*#__PURE__*/React.createElement(SearchIcon, {
    style: {
      color: '#E6616A',
      fontSize: 18
    }
  }), /*#__PURE__*/React.createElement(InputBase, {
    autoFocus: true,
    fullWidth: true,
    value: searchQuery,
    placeholder: searchPlaceholder,
    onChange: event => setSearchQuery(event.target.value),
    inputProps: {
      'aria-label': searchPlaceholder
    }
  }))), none && /*#__PURE__*/React.createElement(MenuItem, {
    key: "none",
    value: ""
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontStyle: 'italic'
    }
  }, noneValue || 'None')), visibleOptions.map((x, i) => /*#__PURE__*/React.createElement(MenuItem, {
    key: i,
    value: x.value
  }, /*#__PURE__*/React.createElement(FlexRow, {
    alignItems: "center",
    margin: 0
  }, circle && x.color && /*#__PURE__*/React.createElement(LittleCircle, {
    backgroundColor: sanitizeColor(x.color),
    marginRight: 2
  }), x.leading && /*#__PURE__*/React.createElement("span", {
    style: {
      display: 'flex',
      flexShrink: 0,
      marginRight: 10
    }
  }, x.leading), x.label)))), throwError && errorMessage && /*#__PURE__*/React.createElement(FormHelperText, null, errorMessage));
  if (xs || md || lg) {
    let marginProps;
    if (margin === 'normal') {
      marginProps = {
        margin: 4,
        marginBottom: 2
      };
    } else if (margin === 'none') {
      marginProps = {
        margin: 0
      };
    } else {
      marginProps = {
        margin
      };
    }
    return /*#__PURE__*/React.createElement(Block, _extends({
      xs: xs,
      md: md,
      lg: lg
    }, marginProps, {
      style: {
        overflowY: 'visible'
      }
    }), content);
  }
  return content;
};
export default SelectOption;